import React, { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Link as RouterLink, useNavigate } from "react-router-dom";
import { slugify } from "../utils/textUtils";
import { ROUTES_NAMES } from "../components/Layout";
import {
  MIS_PODCASTS_MEDIA_KITS,
  misPodcastsMediaKitDetailPath,
} from "../miContenido/paths";
import { ProfileSectionTitle } from "../components/profile/ProfileSectionTitle";
import {
  Box,
  Typography,
  CardMedia,
  Alert,
  TextField,
  Container,
  Grid,
  Button,
  Chip,
  InputAdornment,
  Skeleton,
  Pagination,
  Stack,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import { Add as AddIcon, ChevronRight } from "@mui/icons-material";
import { apiService } from "../services/api";
import { useAuth } from "../contexts/AuthContext";
import { Dropdown, DropdownOption } from "../components/common/Dropdown";
import { getBrandLogoProps, hasBrandLogo } from "../utils/brandLogoUtils";
import { getS3Url } from "../utils/imageUtils";
import ProgressiveImage from "../components/common/ProgressiveImage";
import { useTranslation } from "react-i18next";

// Function to ensure we have a direct S3 URL for PDF generation
const ensureDirectS3Url = (url: string): string => {
  // If it's already a direct S3 URL, return as is
  if (url.includes("podscope.s3.amazonaws.com/")) {
    return url;
  }
  // If it's a proxy URL, convert to direct S3 URL
  if (url.includes("/api/proxy/s3/")) {
    const key = url.split("/api/proxy/s3/")[1];
    return `https://podscope.s3.amazonaws.com/${key}`;
  }
  return url;
};

const sectionActionSx = {
  textTransform: "none",
  fontWeight: 600,
  whiteSpace: "nowrap",
} as const;

const ManageMediaKitsButton: React.FC<{ disabled?: boolean }> = ({
  disabled,
}) => {
  const { t } = useTranslation("miContenido");
  return (
  <Button
    component={RouterLink}
    to={MIS_PODCASTS_MEDIA_KITS}
    variant="text"
    color="primary"
    disabled={disabled}
    sx={sectionActionSx}
  >
    {t("actions.manageMediaKits")}
  </Button>
  );
};

const CreateCollectionButton: React.FC<{
  disabled?: boolean;
  onClick?: () => void;
}> = ({ disabled, onClick }) => {
  const { t } = useTranslation("miContenido");
  return (
  <Button
    variant="text"
    color="primary"
    startIcon={<AddIcon />}
    disabled={disabled}
    onClick={onClick}
    sx={sectionActionSx}
  >
    {t("actions.createCollection")}
  </Button>
  );
};

// Skeleton for podcasts section
const PodcastsSkeleton: React.FC<{ showMediaKits: boolean }> = ({
  showMediaKits,
}) => {
  const { t } = useTranslation("miContenido");
  return (
  <Stack spacing={3}>
    <ProfileSectionTitle
      title={t("sections.podcasts")}
      action={showMediaKits ? <ManageMediaKitsButton disabled /> : null}
    />
    <Grid container spacing={2}>
      {Array.from({ length: 6 }).map((_, index) => (
        <Grid size={{ xs: 6, sm: 4, md: 3, lg: 2 }} key={index}>
          <Box>
            <Skeleton
              variant="rectangular"
              width="100%"
              height={160}
              sx={{ borderRadius: 1, mb: 1 }}
            />
            <Skeleton variant="text" width="100%" height={20} />
            <Skeleton variant="text" width="60%" height={16} />
          </Box>
        </Grid>
      ))}
    </Grid>
  </Stack>
  );
};

// Skeleton for collections section
const CollectionsSkeleton: React.FC<{ isCompanyAdmin: boolean }> = ({
  isCompanyAdmin,
}) => {
  const { t } = useTranslation("miContenido");
  return (
  <Stack spacing={3}>
    <ProfileSectionTitle
      title={t("sections.collections")}
      action={isCompanyAdmin ? <CreateCollectionButton disabled /> : null}
    />

    {/* Search and Filters Row */}
    <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
      {/* Search Bar */}
      <TextField
        placeholder={t("filters.searchCollections")}
        value=""
        size="small"
        sx={{ flex: 1 }}
        disabled
        InputProps={{
          startAdornment: (
            <InputAdornment position="start">
              <SearchIcon />
            </InputAdornment>
          ),
        }}
      />

      {/* Brand Filter */}
      <Dropdown
        label={t("filters.filterByBrand")}
        value=""
        options={[]}
        onChange={() => {}}
        emptyLabel={t("filters.allBrands")}
        width={200}
        disabled
      />

      {/* Podcast Filter */}
      <Dropdown
        label={t("filters.filterByPodcast")}
        value=""
        options={[]}
        onChange={() => {}}
        emptyLabel={t("filters.allPodcasts")}
        width={220}
        disabled
      />
    </Box>

    {/* Collection Cards Grid */}
    <Grid container spacing={2}>
      {Array.from({ length: 4 }).map((_, index) => (
        <Grid size={{ xs: 12, sm: 6, md: 4, lg: 3 }} key={index}>
          <Box>
            <Skeleton
              variant="rectangular"
              width="100%"
              height={160}
              sx={{ borderRadius: 1, mb: 1 }}
            />
            <Skeleton variant="text" width="80%" height={24} />
            <Skeleton variant="text" width="50%" height={20} />
          </Box>
        </Grid>
      ))}
    </Grid>
  </Stack>
  );
};

const MyPodcasts: React.FC = () => {
  const { t } = useTranslation("miContenido");
  const navigate = useNavigate();
  const { user, isCompanyAdmin } = useAuth();
  const myCompanyId = user?.companyId;

  // Filter states for collections
  const [selectedBrandId, setSelectedBrandId] = useState<string>("");
  const [selectedPodcastId, setSelectedPodcastId] = useState<string>("");
  const [searchTerm, setSearchTerm] = useState<string>("");

  // Pagination state for collections
  const [currentPage, setCurrentPage] = useState<number>(1);
  const itemsPerPage = 12;

  // Then get all company podcasts
  const {
    data: podcastsData,
    isLoading,
    error,
  } = useQuery({
    queryKey: ["my-podcasts", myCompanyId],
    queryFn: () => apiService.getCompanyPodcasts(myCompanyId || ""),
    enabled: !!myCompanyId,
    placeholderData: (previousData) => previousData,
    staleTime: 1000 * 60 * 5, // 5 min
    gcTime: 1000 * 60 * 5, // 5 min
  });

  // Get company collections with backend pagination
  const {
    data: collectionsData,
    isLoading: collectionsLoading,
    error: collectionsError,
  } = useQuery({
    queryKey: [
      "company-collections",
      myCompanyId,
      currentPage,
      searchTerm,
      selectedPodcastId,
      selectedBrandId,
    ],
    queryFn: () =>
      apiService.getCompanyCollections(
        currentPage,
        itemsPerPage,
        searchTerm || undefined,
        selectedPodcastId || undefined,
        selectedBrandId || undefined,
      ),
    enabled: !!myCompanyId,
    placeholderData: (previousData) => previousData,
    staleTime: 1000 * 60 * 2, // 2 min (shorter since we have pagination)
    gcTime: 1000 * 60 * 5, // 5 min
  });

  const podcasts = podcastsData?.podcasts || [];
  const collections = collectionsData?.collections || [];

  // Fetch first content item for each collection to get the image
  const { data: collectionsWithFirstContent } = useQuery({
    queryKey: ["collections-first-content", collections.map((c) => c._id)],
    queryFn: async () => {
      const collectionsWithContent = await Promise.all(
        collections.map(async (collection) => {
          try {
            const content = await apiService.getPrivateCollectionGroupedContent(
              collection._id,
            );
            // Prioritize YouTube videos, then fall back to first available content
            const firstContent =
              content.allContent.youtubeContent?.[0] ||
              content.allContent.instagramContent?.[0] ||
              content.allContent.tiktokContent?.[0] ||
              content.allContent.spotifyContent?.[0] ||
              content.allContent.facebookContent?.[0] ||
              null;

            // Store all content IDs for this collection for filtering
            const allContentIds = [
              ...(content.allContent.instagramContent || []).map(
                (item) => item._id,
              ),
              ...(content.allContent.tiktokContent || []).map(
                (item) => item._id,
              ),
              ...(content.allContent.youtubeContent || []).map(
                (item) => item._id,
              ),
              ...(content.allContent.spotifyContent || []).map(
                (item) => item._id,
              ),
              ...(content.allContent.facebookContent || []).map(
                (item) => item._id,
              ),
            ];

            return {
              ...collection,
              firstContentImage: firstContent?.imageS3Key || null,
              contentIds: allContentIds,
            };
          } catch (error) {
            console.warn(
              `Failed to fetch content for collection ${collection._id}:`,
              error,
            );
            return {
              ...collection,
              firstContentImage: null,
              contentIds: [],
            };
          }
        }),
      );
      return collectionsWithContent;
    },
    enabled: collections.length > 0,
    staleTime: 1000 * 60 * 5, // 5 min
    gcTime: 1000 * 60 * 5, // 5 min
  });

  // Fetch brands for filtering (now just to get the list for dropdown, filtering is done on backend)
  const { data: brandsData } = useQuery({
    queryKey: ["brands-for-collection-filter", myCompanyId],
    queryFn: async () => {
      if (!myCompanyId) return [];

      const allBrands = new Map();

      // Get all company podcasts
      const companyPodcasts = await apiService.getCompanyPodcasts(myCompanyId);

      for (const podcast of companyPodcasts.podcasts) {
        try {
          // Get all content IDs for this podcast
          const podcastContent = await apiService.getPrivateCollectionsContent(
            podcast._id,
          );
          const contentIds = [
            ...(podcastContent.allContent.instagramContent || []).map(
              (item) => item._id,
            ),
            ...(podcastContent.allContent.tiktokContent || []).map(
              (item) => item._id,
            ),
            ...(podcastContent.allContent.youtubeContent || []).map(
              (item) => item._id,
            ),
            ...(podcastContent.allContent.spotifyContent || []).map(
              (item) => item._id,
            ),
            ...(podcastContent.allContent.facebookContent || []).map(
              (item) => item._id,
            ),
          ];

          if (contentIds.length > 0) {
            // Get brand mentions for this podcast's content
            const brandMentions =
              await apiService.getPrivateBrandMentions(contentIds);

            // Extract unique brands
            Object.values(brandMentions)
              .flat()
              .forEach((mention: any) => {
                if (mention.brandId && mention.brandName) {
                  allBrands.set(mention.brandId, {
                    _id: mention.brandId,
                    name: mention.brandName,
                    logoUrl: mention.brand?.logoUrl,
                    s3Key: mention.brand?.s3Key,
                  });
                }
              });
          }
        } catch (error) {
          console.warn(
            `Failed to fetch brands for podcast ${podcast._id}:`,
            error,
          );
        }
      }

      return Array.from(allBrands.values());
    },
    enabled: !!myCompanyId,
    staleTime: 1000 * 60 * 5, // 5 minutes
  });

  // Filter podcasts locally since we have all of them
  const filteredPodcasts = podcasts.filter((p) => true);

  // Get collections and pagination from backend response
  const collectionsToDisplay = collectionsWithFirstContent || collections;
  const pagination = collectionsData?.pagination;
  const totalPages = pagination?.totalPages || 1;
  const totalCollections = pagination?.total || collections.length;

  // Reset to page 1 when filters change
  React.useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm, selectedBrandId, selectedPodcastId]);

  const isInitialLoading = isLoading;

  return (
    <>
      <Container maxWidth="xl">
        <Stack
          direction="row"
          alignItems="center"
          sx={{
            mt: 2,
            mb: 2.5,
            borderBottom: 1,
            borderColor: "divider",
            minHeight: 48,
          }}
        >
          <Typography variant="h1" component="h1" sx={{ pb: { xs: 0, md: 1 } }}>
            {t("sections.myPodcasts")}
          </Typography>
        </Stack>

        {error && (
          <Alert severity="error" sx={{ mb: 3 }}>
            {t("errors.loadPodcasts")}
          </Alert>
        )}

        {collectionsError && (
          <Alert severity="error" sx={{ mb: 3 }}>
            {t("errors.loadCollections")}
          </Alert>
        )}

        <Stack spacing={5} sx={{ mb: 4 }}>
          {/* Podcasts Section */}
          {isInitialLoading ? (
            <PodcastsSkeleton showMediaKits={Boolean(myCompanyId)} />
          ) : filteredPodcasts.length > 0 ? (
            <Stack spacing={3}>
              <ProfileSectionTitle
                title={t("sections.podcasts")}
                action={myCompanyId ? <ManageMediaKitsButton /> : null}
              />
              <Grid container spacing={2}>
                {filteredPodcasts.map((podcast) => {
                  const podcastSlug = podcast.slug || slugify(podcast.title);
                  const mediakitEstadoLabel =
                    podcast.mediaKitEnabled === true
                      ? t("status.active")
                      : podcast.mediaKitEnabled === false
                        ? t("status.inactive")
                        : t("status.unknown");
                  return (
                    <Grid
                      size={{ xs: 6, sm: 4, md: 3, lg: 2 }}
                      key={podcast._id}
                    >
                      <Box
                        key={podcast._id}
                        onClick={() => navigate(`/mis-podcasts/${podcastSlug}`)}
                        sx={{
                          display: "flex",
                          flexDirection: "column",
                          position: "relative",
                          transition: "all 0.2s ease-in-out",
                          cursor: "pointer",
                          overflow: "visible",
                        }}
                      >
                        <CardMedia
                          component="img"
                          image={
                            podcast.imageS3Key
                              ? ensureDirectS3Url(getS3Url(podcast.imageS3Key))
                              : podcast.imageUrl ||
                                podcast.youtubeData?.snippet?.thumbnails?.high
                                  ?.url
                          }
                          alt={podcast.title}
                          height="140"
                          width="140"
                          sx={{
                            width: "100%",
                            objectFit: "cover",
                            borderRadius: 1,
                            height: 160,
                          }}
                        />

                        <Box sx={{ pt: 1 }}>
                          <Typography
                            variant="h3"
                            sx={{
                              fontSize: "1rem",
                              color: "primary.main",
                              overflow: "hidden",
                              textOverflow: "ellipsis",
                              whiteSpace: "nowrap",
                              lineHeight: 1.2,
                            }}
                          >
                            {podcast.title}
                          </Typography>
                          <Button
                            type="button"
                            variant="text"
                            fullWidth
                            aria-label={t("status.configureMediakitAria", {
                              title: podcast.title,
                              status: mediakitEstadoLabel,
                            })}
                            onClick={(e) => {
                              e.stopPropagation();
                              navigate(
                                misPodcastsMediaKitDetailPath(
                                  String(podcast._id),
                                ),
                              );
                            }}
                            sx={{
                              px: 0.75,
                              mx: -0.75,
                              py: 0.5,
                              minWidth: 0,
                              width: "105%",
                              justifyContent: "stretch",
                              textTransform: "none",
                              fontWeight: 400,
                              borderRadius: 1,
                              "&:hover": {
                                bgcolor: "action.hover",
                              },
                            }}
                          >
                            <Box
                              sx={{
                                display: "flex",
                                alignItems: "center",
                                gap: 0.75,
                                width: "100%",
                                minWidth: 0,
                              }}
                            >
                              <Typography
                                variant="body2"
                                color="text.secondary"
                                sx={{
                                  flexShrink: 0,
                                  lineHeight: 1.2,
                                  fontWeight: 400,
                                  fontSize: "0.75rem",
                                }}
                              >
                                {t("status.mediakit")}
                              </Typography>
                              <Box sx={{ flex: 1, minWidth: 4 }} />
                              {podcast.mediaKitEnabled === true && (
                                <Chip
                                  component="span"
                                  size="small"
                                  label={t("status.active")}
                                  color="success"
                                  variant="outlined"
                                  sx={{
                                    pointerEvents: "none",
                                    flexShrink: 0,
                                    height: 18,
                                    "& .MuiChip-label": {
                                      px: 0.45,
                                      py: 0,
                                      fontSize: "0.625rem",
                                      lineHeight: 1.2,
                                      fontWeight: 500,
                                    },
                                  }}
                                />
                              )}
                              {podcast.mediaKitEnabled === false && (
                                <Chip
                                  component="span"
                                  size="small"
                                  label={t("status.inactive")}
                                  variant="outlined"
                                  sx={{
                                    pointerEvents: "none",
                                    flexShrink: 0,
                                    height: 18,
                                    "& .MuiChip-label": {
                                      px: 0.45,
                                      py: 0,
                                      fontSize: "0.625rem",
                                      lineHeight: 1.2,
                                      fontWeight: 500,
                                    },
                                  }}
                                />
                              )}
                              {podcast.mediaKitEnabled !== true &&
                                podcast.mediaKitEnabled !== false && (
                                  <Chip
                                    component="span"
                                    size="small"
                                    label="—"
                                    variant="outlined"
                                    sx={{
                                      pointerEvents: "none",
                                      flexShrink: 0,
                                      height: 18,
                                      "& .MuiChip-label": {
                                        px: 0.45,
                                        py: 0,
                                        fontSize: "0.625rem",
                                        lineHeight: 1.2,
                                      },
                                    }}
                                  />
                                )}
                              <ChevronRight
                                sx={{
                                  fontSize: 16,
                                  color: "text.disabled",
                                  flexShrink: 0,
                                }}
                                aria-hidden
                              />
                            </Box>
                          </Button>
                        </Box>
                      </Box>
                    </Grid>
                  );
                })}
              </Grid>
            </Stack>
          ) : null}

          {/* Collections Section */}
          {collectionsLoading ? (
            <CollectionsSkeleton isCompanyAdmin={isCompanyAdmin} />
          ) : !isInitialLoading ? (
            <Stack spacing={3}>
              <ProfileSectionTitle
                title={t("sections.collections")}
                action={
                  isCompanyAdmin ? (
                    <CreateCollectionButton
                      onClick={() => navigate(ROUTES_NAMES.CREATE_COLLECTION)}
                    />
                  ) : null
                }
              />

              {/* Search and Filters Row */}
              <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                {/* Search Bar */}
                <TextField
                  placeholder={t("filters.searchCollections")}
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  size="small"
                  sx={{ flex: 1 }}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <SearchIcon />
                      </InputAdornment>
                    ),
                  }}
                />

                {/* Brand Filter */}
                <Dropdown
                  label={t("filters.filterByBrand")}
                  value={selectedBrandId}
                  options={
                    brandsData?.map((brand): DropdownOption => {
                      return {
                        value: brand._id,
                        label: brand.name,
                        avatar: hasBrandLogo(brand)
                          ? getBrandLogoProps(brand).src
                          : undefined,
                      };
                    }) || []
                  }
                  onChange={(value) => setSelectedBrandId(value || "")}
                  emptyLabel={t("filters.allBrands")}
                  width={200}
                />

                {/* Podcast Filter */}
                <Dropdown
                  label={t("filters.filterByPodcast")}
                  value={selectedPodcastId}
                  options={
                    podcasts?.map((podcast): DropdownOption => ({
                      value: podcast._id,
                      label: podcast.title,
                      avatar: podcast.imageS3Key
                        ? getS3Url(podcast.imageS3Key)
                        : podcast.imageUrl ||
                          podcast.youtubeData?.snippet?.thumbnails?.high?.url,
                    })) || []
                  }
                  onChange={(value) => setSelectedPodcastId(value || "")}
                  emptyLabel={t("filters.allPodcasts")}
                  width={220}
                />
              </Box>

              {collectionsToDisplay.length === 0 ? (
                <Box sx={{ textAlign: "center", py: 4 }}>
                  <Typography variant="body1" color="text.secondary">
                    {collectionsLoading
                      ? t("empty.loadingCollections")
                      : t("empty.noMatchingCollections")}
                  </Typography>
                </Box>
              ) : (
                <>
                  <Grid container spacing={2}>
                    {collectionsToDisplay.map((collection) => (
                      <Grid
                        size={{ xs: 12, sm: 6, md: 4, lg: 3 }}
                        key={collection._id}
                      >
                        <Box
                          onClick={() => {
                            navigate(
                              `/mis-podcasts/colecciones/${collection._id}`,
                            );
                          }}
                          sx={{
                            position: "relative",
                            cursor: "pointer",
                          }}
                        >
                          {(collection as any).firstContentImage ? (
                            // Show first content image with lazy loading
                            <ProgressiveImage
                              src={ensureDirectS3Url(
                                getS3Url((collection as any).firstContentImage),
                              )}
                              alt={collection.title}
                              sx={{
                                width: "100%",
                                height: 160,
                                objectFit: "cover",
                                borderRadius: 1,
                              }}
                            />
                          ) : (
                            // Fallback - show generic icon
                            <Skeleton
                              variant="rectangular"
                              sx={{
                                width: "100%",
                                borderRadius: 1,
                                height: 160,
                              }}
                            />
                          )}

                          <Box sx={{ pt: 1 }}>
                            <Typography
                              variant="h3"
                              sx={{
                                fontSize: "1rem",
                                color: "primary.main",
                                overflow: "hidden",
                                textOverflow: "ellipsis",
                                whiteSpace: "nowrap",
                              }}
                            >
                              {collection.title}
                            </Typography>
                          </Box>
                        </Box>
                      </Grid>
                    ))}
                  </Grid>

                  {/* Pagination Controls */}
                  {totalPages > 1 && (
                    <Box
                      sx={{
                        display: "flex",
                        justifyContent: "center",
                        mt: 4,
                        mb: 4,
                      }}
                    >
                      <Stack spacing={2} alignItems="center">
                        <Pagination
                          count={totalPages}
                          page={currentPage}
                          onChange={(_, page) => setCurrentPage(page)}
                          color="primary"
                          size="large"
                          showFirstButton
                          showLastButton
                          disabled={collectionsLoading}
                        />
                        <Typography variant="body2" color="text.secondary">
                          {t("pagination.collections", {
                            from: (currentPage - 1) * itemsPerPage + 1,
                            to: Math.min(
                              currentPage * itemsPerPage,
                              totalCollections,
                            ),
                            total: totalCollections,
                          })}
                        </Typography>
                      </Stack>
                    </Box>
                  )}
                </>
              )}
            </Stack>
          ) : null}
        </Stack>
      </Container>
    </>
  );
};

export default MyPodcasts;
